import React, { useMemo } from 'react';
import { Building2, Pickaxe, Trees } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Loader from 'src/shared/components/loader/Loader';
import GnrUpgradePage from 'src/app/portfolio/views/global-nature-risk/GnrUpgradePage';
import storageService from 'src/services/storage-service';
import { GNR_OVERVIEW_INFO_BANNER_DISMISSED_STORAGE_KEY } from 'src/shared/constants/storageKeys';
import InfoMessage from 'src/shared/components/info-message/InfoMessage';
import {
    useGetSiteFilterOptionsQuery,
    useGetPortfolioRiskStatsQuery,
    useGetRiskInsightsSitesQuery,
    useGetPhysicalRiskDistributionQuery,
    type GqlRiskBandLevel,
    type GqlSonBandLevel,
} from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import RiskDistributionHistogram from 'src/app/portfolio/views/global-nature-risk/overview/components/RiskDistributionHistogram';
import useGnrFilters from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrFilters';
import { sanitizeSelectedFilters } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { PortfolioViews } from 'src/routes/routesConfig';
import NatureStatusCard from 'src/shared/components/nature-status-card/NatureStatusCard';
import { useGetPortfolioNatureStatusQuery, useSetPortfolioNatureStatusMutation } from './state/portfolioNatureStatusSlice';
import GnrWelcome from './components/GnrWelcome';
import MethodologySheet from './components/MethodologySheet';
import Top5SitesList from './components/Top5SitesList';
import GnrActiveFilters from '../site-list/components/filters/GnrActiveFilters';
import SiteListFilters from '../site-list/components/filters/SiteListFilters';

const FEATURE_ID = 'risk-insights';

const GnrOverview = () => {
    const [isInfoBannerVisible, setIsInfoBannerVisible] = React.useState(false);
    const [isMethodologyOpen, setIsMethodologyOpen] = React.useState(false);
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });
    const { currentCustomerId } = useCustomer();
    const { selectedFilters, applyFilters, resetFilters, updateFilters } = useGnrFilters();

    const impactsAndDependenciesPath = navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_IMPACTS_AND_DEPENDENCIES]({
        customerId: currentCustomerId ?? '',
    });
    const stateOfNaturePath = navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_STATE_OF_NATURE]({ customerId: currentCustomerId ?? '' });
    const { data: natureStatusData, isLoading: isLoadingNatureStatus } = useGetPortfolioNatureStatusQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );
    const [setPortfolioNatureStatus] = useSetPortfolioNatureStatusMutation();

    React.useEffect(() => {
        const isBannerDismissed = Boolean(storageService.get(GNR_OVERVIEW_INFO_BANNER_DISMISSED_STORAGE_KEY));
        setIsInfoBannerVisible(!isBannerDismissed);
    }, []);

    const handleBannerDismiss = React.useCallback(() => {
        storageService.save(GNR_OVERVIEW_INFO_BANNER_DISMISSED_STORAGE_KEY, true);
        setIsInfoBannerVisible(false);
    }, []);

    const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetSiteFilterOptionsQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );

    const siteFilterOptions = filterOptionsData?.organisation?.siteFilterOptions;

    const validatedFilters = useMemo(
        () => sanitizeSelectedFilters(selectedFilters, siteFilterOptions),
        [selectedFilters, siteFilterOptions]
    );

    const isicFilter = validatedFilters.find(f => f.name === 'isic')?.values;
    const countryFilter = validatedFilters.find(f => f.name === 'country')?.values;
    const physicalRiskLevels = validatedFilters.find(f => f.name === 'physical_risk_levels')?.values as GqlRiskBandLevel[] | undefined;
    const dependencyLevels = validatedFilters.find(f => f.name === 'dependency_levels')?.values as GqlRiskBandLevel[] | undefined;
    const impactLevels = validatedFilters.find(f => f.name === 'impact_levels')?.values as GqlRiskBandLevel[] | undefined;
    const sonLevels = validatedFilters.find(f => f.name === 'son_levels')?.values as GqlSonBandLevel[] | undefined;

    const filterArgs = {
        isic: isicFilter,
        countries: countryFilter,
        physicalRiskLevels,
        dependencyLevels,
        impactLevels,
        sonLevels,
    };

    const { data: distributionData, isLoading: isLoadingDistribution } = useGetPhysicalRiskDistributionQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const distributionBins = distributionData?.organisation?.physicalRiskDistribution ?? [];

    const { data: portfolioStatsData, isLoading: isLoadingStats } = useGetPortfolioRiskStatsQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const stats = portfolioStatsData?.organisation?.portfolioRiskStats;

    const topSiteBase = {
        businessCentralId: currentCustomerId,
        page: 1,
        pageSize: 5,
        isic: isicFilter,
        country: countryFilter,
        physicalRiskLevels,
        dependencyLevels,
        impactLevels,
        sonLevels,
    };

    const { currentData: depSitesData, isFetching: isLoadingDepSites } = useGetRiskInsightsSitesQuery(
        { ...topSiteBase, sortBy: 'OPERATIONAL_DEPENDENCIES', sortDir: 'DESC' },
        { skip: !currentCustomerId }
    );
    const { currentData: impactSitesData, isFetching: isLoadingImpactSites } = useGetRiskInsightsSitesQuery(
        { ...topSiteBase, sortBy: 'IMPACTS_ON_NATURE', sortDir: 'DESC' },
        { skip: !currentCustomerId }
    );
    const { currentData: sonSitesData, isFetching: isLoadingSonSites } = useGetRiskInsightsSitesQuery(
        { ...topSiteBase, sortBy: 'STATE_OF_NATURE', sortDir: 'ASC' },
        { skip: !currentCustomerId }
    );

    const depSites = depSitesData?.organisation?.sites?.items ?? [];
    const impactSites = impactSitesData?.organisation?.sites?.items ?? [];
    const sonSites = sonSitesData?.organisation?.sites?.items ?? [];

    if (loading) return <Loader />;
    if (!enabled) return <GnrUpgradePage />;

    return (
        <>
            <StandardAppLayout
                title='Global Nature Risk'
                subTitle={
                    stats?.assessedSiteCount
                        ? `Overview of nature-related risks across ${stats.assessedSiteCount.toLocaleString()} assessed sites.`
                        : 'Overview of nature-related risks across your assessed sites.'
                }
                stickyHeader={false}
                classNames={{ otherActions: '!justify-start !p-0' }}
                otherActions={
                    <SiteListFilters
                        selectedFilters={selectedFilters}
                        onApply={applyFilters}
                        onClear={resetFilters}
                        filterOptions={siteFilterOptions}
                        isLoadingFilterOptions={isLoadingFilterOptions}
                        businessCentralId={currentCustomerId ?? undefined}
                    />
                }
                mainContent={
                    <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='gnr-overview-main'>
                        <GnrActiveFilters
                            selectedFilters={validatedFilters}
                            countries={siteFilterOptions?.countries}
                            isicCodes={siteFilterOptions?.isicCodes}
                            onRemove={updateFilters}
                            onClearAll={resetFilters}
                            matchingSiteCount={stats?.assessedSiteCount}
                        />

                        {/* Physical Nature Risk section */}
                        <div className='grid grid-cols-3 gap-4'>
                            <RiskScoreCard
                                title='Physical Nature Risk'
                                value={stats?.meanPhysicalNatureRisk ?? null}
                                scoreType='Risk'
                                isLoading={isLoadingStats}
                                size='lg'
                                slug='physical-nature-risk'
                                explainerSlug='physical-nature-risk-te'
                            />
                            <div className='col-span-2 h-full'>
                                <NatureStatusCard
                                    text={natureStatusData?.organisation?.natureStatus?.content ?? null}
                                    createdAt={natureStatusData?.organisation?.natureStatus?.createdAt ?? null}
                                    isLoading={isLoadingNatureStatus}
                                    onSave={async content => {
                                        await setPortfolioNatureStatus({ businessCentralId: currentCustomerId, content }).unwrap();
                                    }}
                                />
                            </div>
                        </div>

                        {/* Risk distribution histogram */}
                        <div className='bg-white shadow-portfolio-summary-card rounded-lg p-4 xl:p-5'>
                            <RiskDistributionHistogram bins={distributionBins} isLoading={isLoadingDistribution} />
                        </div>

                        {isInfoBannerVisible && (
                            <InfoMessage
                                isDismissable
                                onDismiss={handleBannerDismiss}
                                message={
                                    <>
                                        Physical Nature Risk score combines site-specific inputs using a geometric mean across three key
                                        components: Dependencies, Impact on Nature and State of Nature. Click to{' '}
                                        <button
                                            type='button'
                                            className='underline bg-transparent border-0 p-0 cursor-pointer'
                                            onClick={() => setIsMethodologyOpen(true)}
                                        >
                                            learn more
                                        </button>
                                        .
                                    </>
                                }
                            />
                        )}

                        {/* Contributors section */}
                        <div className='flex flex-col gap-4'>
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-3'>
                                    <span className='text-xl text-black'>Physical Nature Risk contributors</span>
                                    <button
                                        type='button'
                                        data-testid='contributors-learn-more-btn'
                                        className='border border-border rounded-lg px-2 py-0.5 text-xs text-foreground'
                                        onClick={() => setIsMethodologyOpen(true)}
                                    >
                                        Learn more
                                    </button>
                                </div>
                                <ReportingFrameworks slugPrefix='gnr' />
                            </div>

                            <div className='grid grid-cols-3 gap-4'>
                                {/* Dependencies column */}
                                <div className='flex flex-col gap-1'>
                                    <RiskScoreCard
                                        title='Dependencies'
                                        value={stats?.meanOperationalDependencies ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoadingStats}
                                        size='md'
                                        icon={Building2}
                                        slug='dependencies'
                                        explainerSlug='dependencies-te'
                                        exploreTo={impactsAndDependenciesPath}
                                    />
                                    <Top5SitesList
                                        title='Top 5 Operational Risk Sites'
                                        sites={depSites}
                                        scoreKey='operationalDependencies'
                                        direction='higher-worse'
                                        scoreType='Risk'
                                        isLoading={isLoadingDepSites}
                                        slug='gnr-top5-sites-operational-risk'
                                        customerId={currentCustomerId}
                                    />
                                </div>

                                {/* Impact on Nature column */}
                                <div className='flex flex-col gap-1'>
                                    <RiskScoreCard
                                        title='Impact on Nature'
                                        value={stats?.meanImpactsOnNature ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoadingStats}
                                        size='md'
                                        icon={Pickaxe}
                                        slug='impact-on-nature'
                                        explainerSlug='impact-on-nature-te'
                                        exploreTo={impactsAndDependenciesPath}
                                    />
                                    <Top5SitesList
                                        title='Top 5 Reputational Risk Sites'
                                        sites={impactSites}
                                        scoreKey='impactsOnNature'
                                        direction='higher-worse'
                                        scoreType='Risk'
                                        isLoading={isLoadingImpactSites}
                                        slug='gnr-top5-sites-reputational-risk'
                                        customerId={currentCustomerId}
                                    />
                                </div>

                                {/* State of Nature column */}
                                <div className='flex flex-col gap-1'>
                                    <RiskScoreCard
                                        title='State of Nature'
                                        value={stats?.meanStateOfNature ?? null}
                                        isLoading={isLoadingStats}
                                        size='md'
                                        icon={Trees}
                                        slug='state-of-nature'
                                        explainerSlug='state-of-nature-te'
                                        scoreType='Condition'
                                        exploreTo={stateOfNaturePath}
                                    />
                                    <Top5SitesList
                                        title='Top 5 Nature Opportunity Sites'
                                        sites={sonSites}
                                        scoreKey='stateOfNature'
                                        direction='higher-better'
                                        scoreType='Condition'
                                        isLoading={isLoadingSonSites}
                                        slug='gnr-top5-sites-nature-opportunity'
                                        customerId={currentCustomerId}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                }
            />
            <GnrWelcome onExploreMethodology={() => setIsMethodologyOpen(true)} />
            <MethodologySheet open={isMethodologyOpen} onOpenChange={setIsMethodologyOpen} />
        </>
    );
};

export default GnrOverview;
